import React from 'react';
import {
  Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem, useTheme, alpha,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * DataPreprocessingPanel
 *
 * Compact preprocessing config. Surfaces just the ONE knob 99% of
 * users care about (the global scaler) and tucks per-signal-type
 * overrides behind a nested collapsible.
 *
 * Uses ONLY theme tokens (no hardcoded colors).
 */
const DataPreprocessingPanel = ({ value, onChange }) => {
  const theme = useTheme();
  const dp = value || {};
  const signalScalers = dp.signal_scalers || {};

  const SCALER_OPTIONS = [
    { value: 'MinMaxScaler', label: 'MinMaxScaler — scales to [0, 1] (recommended for NNs)' },
    { value: 'StandardScaler', label: 'StandardScaler — zero mean, unit variance' },
    { value: 'RobustScaler', label: 'RobustScaler — handles outliers' },
    { value: 'MaxAbsScaler', label: 'MaxAbsScaler — preserves sparsity' },
    { value: 'None', label: 'No scaling — pass values through unchanged' },
  ];

  const SIGNAL_TYPES = [
    { key: 'numeric',     label: 'Numeric',     hint: 'Continuous values' },
    { key: 'categorical', label: 'Categorical', hint: 'Discrete labels' },
    { key: 'timestamp',   label: 'Timestamp',   hint: 'Date/time values' },
    { key: 'text',        label: 'Text',        hint: 'Free-form text' },
    { key: 'binary',      label: 'Binary',      hint: '0/1 booleans' },
  ];

  const setGlobalScaler = (v) => onChange({ ...dp, global_scaler: v });
  const setSignalScaler = (key, v) => onChange({
    ...dp,
    signal_scalers: { ...signalScalers, [key]: v },
  });

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scaling normalizes your data so the model trains faster and more reliably.
        The default works for most forecasting tasks.
      </Typography>

      <FormControl fullWidth sx={{ mb: 1, maxWidth: 480 }}>
        <InputLabel id="global-scaler-label">Default Scaler</InputLabel>
        <Select
          labelId="global-scaler-label"
          value={dp.global_scaler || 'MinMaxScaler'}
          onChange={(e) => setGlobalScaler(e.target.value)}
          label="Default Scaler"
        >
          {SCALER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Accordion
        sx={{
          mt: 2,
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Per-type scaler overrides
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Override the default for specific data types. Leave blank to inherit from the default.
          </Typography>
          <Grid container spacing={2}>
            {SIGNAL_TYPES.map((pt) => (
              <Grid item xs={12} sm={6} md={4} key={pt.key}>
                <FormControl fullWidth size="small">
                  <InputLabel>{pt.label}</InputLabel>
                  <Select
                    label={pt.label}
                    value={signalScalers[pt.key] || ''}
                    onChange={(e) => setSignalScaler(pt.key, e.target.value)}
                  >
                    <MenuItem value=""><em>Use default</em></MenuItem>
                    {SCALER_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.value}</MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {pt.hint}
                  </Typography>
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default DataPreprocessingPanel;
